'use client'

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { saveQuizSubmission } from '../services/firebase';
import { useAuthContext } from '../hooks/useAuthContext';

const EmployersPage = () => {
  const { user, userProfile, authIsReady, profileLoading } = useAuthContext();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    roles: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Redirect logged-in users to appropriate page
  useEffect(() => {
    if (authIsReady && user) {
      // If user needs onboarding, redirect to onboarding
      if (userProfile && userProfile.onboardingComplete === false) {
        navigate('/onboarding');
      } else {
        // Otherwise redirect to network
        navigate('/network');
      }
    }
  }, [user, userProfile, authIsReady, navigate]);

  // Show loading while checking authentication
  if (!authIsReady || (user && profileLoading)) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // Don't render the landing page if user is authenticated (they'll be redirected)
  if (user) {
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    
    if (!formData.companyName.trim()) {
      errors.companyName = 'Company name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.roles.trim()) {
      errors.roles = 'Please describe the roles you are hiring for';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRequestAccessSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare submission data for Firestore with employer-specific structure
      const submissionData = {
        employerInfo: {
          fullName: formData.fullName,
          companyName: formData.companyName,
          email: formData.email,
          roles: formData.roles,
        },
        completedAt: new Date().toISOString(),
        source: 'employer_access_request', // Critical identifier for employer requests
      };

      // Save to Firestore using existing saveQuizSubmission function
      const result = await saveQuizSubmission(submissionData);
      
      if (result.success) {
        console.log('Employer access request saved with ID:', result.id);
        setShowSuccess(true);
        setFormData({
          fullName: '',
          companyName: '',
          email: '',
          roles: ''
        });
      } else {
        console.error('Failed to save employer access request:', result.error);
        // Still show success to user but log the error
        setShowSuccess(true);
        setFormData({
          fullName: '',
          companyName: '',
          email: '',
          roles: ''
        });
      }
    } catch (error) {
      console.error('Error submitting employer access request:', error);
      // Still show success to user but log the error
      setShowSuccess(true);
      setFormData({
        fullName: '',
        companyName: '',
        email: '',
        roles: ''
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <header className="absolute inset-x-0 top-0 z-50">
        <nav aria-label="Global" className="flex items-center justify-between p-6 lg:px-8">
          <div className="flex lg:flex-1">
            <Link to="/" className="-m-1.5 p-1.5 flex items-center gap-2">
              <span className="sr-only">Burnt</span>
              <img src="/burnt-logo.svg" alt="Burnt Logo" className="h-6" />
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent">burnt</span>
            </Link>
          </div>
          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="hidden lg:flex lg:gap-x-12">
            <Link to="/" className="text-sm/6 font-semibold text-gray-900 hover:text-purple-600 transition-colors underline decoration-purple-600/30 hover:decoration-purple-600 underline-offset-4">
              Are you the top 1%? Join our network
            </Link>
          </div>
          <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          </div>
        </nav>
        <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
          <div className="fixed inset-0 z-50" />
          <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between">
              <Link to="/" className="-m-1.5 p-1.5 flex items-center gap-2">
                <span className="sr-only">Burnt</span>
                <img src="/burnt-logo.svg" alt="Burnt Logo" className="h-6" />
                <span className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent">burnt</span>
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="-m-2.5 rounded-md p-2.5 text-gray-700"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="py-6">
                  <Link
                    to="/"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Are you the top 1%? Join our network
                  </Link>
                </div>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </header>

      {/* Hero Section */}
      <div className="relative isolate px-6 pt-14 lg:px-8">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
        >
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className="relative left-[calc(50%-11rem)] aspect-1155/678 w-144.5 -translate-x-1/2 rotate-30 bg-linear-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-288.75"
          />
        </div>

        {/* Main hero content - Two column layout */}
        <div className="mx-auto max-w-7xl py-32 sm:py-48 lg:py-56">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Column - Main Heading */}
            <div>
              <h1 className="text-5xl font-semibold tracking-tight text-balance text-gray-900 sm:text-7xl">
                Hire the Top 1% of Talent
              </h1>
              <p className="mt-8 text-lg font-medium text-pretty text-gray-500 sm:text-xl/8">
                Stop sifting through noise. Get direct access to a private, vetted network of high-achievers ready for their next big role.
              </p>
            </div>

            {/* Right Column - Featured Profile Card */}
            <div className="relative">
              {/* Square dot pattern background */}
              <div className="absolute inset-0 -m-8 opacity-30">
                <svg
                  className="h-full w-full"
                  fill="none"
                  viewBox="0 0 400 400"
                  aria-hidden="true"
                >
                  <defs>
                    <pattern
                      id="square-pattern"
                      x="0"
                      y="0"
                      width="40"
                      height="40"
                      patternUnits="userSpaceOnUse"
                    >
                      <circle
                        cx="20"
                        cy="20"
                        r="2"
                        fill="#a855f7"
                        opacity="0.4"
                      />
                    </pattern>
                  </defs>
                  <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="url(#square-pattern)"
                  />
                </svg>
              </div>
              
              <div className="relative z-10 rounded-2xl shadow-2xl overflow-hidden bg-white/90 backdrop-blur-sm ring-1 ring-gray-200/50">
                <div className="p-6">
                  {/* Profile Header */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="aspect-square h-20 w-20 rounded-xl overflow-hidden bg-gray-100 ring-1 ring-gray-200">
                      <img
                        className="h-full w-full object-cover"
                        src="/img/candidate-headshots/StephanieM_headshot.jpg"
                        alt="Stephanie M."
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900">
                        Stephanie M.
                      </h3>
                      <p className="text-xs text-gray-600">
                        Seeking VP Strategic Finance role • $150k-180k
                      </p>
                    </div>
                  </div>

                  {/* Stephanie's Introduction */}
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4">
                    <p className="text-sm leading-relaxed text-gray-700">
                      "As an investment banker, I've advised on $12B+ in M&A deals, uncovered $230M in value through strategic modeling, and advised C-suite executives. Since I've been a kid, I've always wanted to work at a start-up."
                    </p>
                  </div>

                  {/* Key Outcomes */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold mb-3 text-gray-700">
                      Key Outcomes:
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                        <span>Led $2.3B acquisition deal</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                        <span>Built financial models saving $230M</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                        <span>Harvard MBA, 8+ years experience</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
        >
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className="relative left-[calc(50%+3rem)] aspect-1155/678 w-144.5 -translate-x-1/2 bg-linear-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%+36rem)] sm:w-288.75"
          />
        </div>
      </div>

      {/* Request Access Form Section */}
      <div className="relative bg-gradient-to-br from-gray-50 via-white to-purple-50/30 py-24 sm:py-32 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-x-0 -top-16 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-24">
          <div
            style={{
              clipPath: 'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className="relative left-1/2 -z-10 aspect-1155/678 w-144.5 max-w-none -translate-x-1/2 rotate-30 bg-gradient-to-tr from-purple-300/20 to-pink-300/20 opacity-40 sm:left-[calc(50%-40rem)] sm:w-288.75"
          />
        </div>
        
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-8">
              <span className="inline-flex items-center rounded-full bg-purple-100 px-4 py-2 text-sm font-semibold text-purple-700 ring-1 ring-purple-700/10">
                🚀 Partner with Us
              </span>
            </div>
            <h2 className="text-4xl font-bold tracking-tight sm:text-6xl leading-none pb-4">
              <span className="bg-gradient-to-r from-gray-900 via-purple-900 to-gray-900 bg-clip-text text-transparent">
                Request Access to the Talent Pool
              </span>
            </h2>
            <p className="mt-8 text-xl leading-8 text-gray-600 max-w-2xl mx-auto">
              Join leading companies who partner with Burnt to access pre-vetted top performers. No more endless resume screening – just quality matches.
            </p>
          </div>
          
          <div className="mx-auto mt-16 max-w-2xl">
            <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 sm:p-12 shadow-2xl ring-1 ring-gray-900/5">
              {showSuccess ? (
                <div className="text-center py-8">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-6">
                    <CheckIcon className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Received! 🎉</h3>
                  <p className="text-lg text-gray-600 mb-4">Thanks for your interest! Our partnerships team will be in touch shortly.</p>
                  <p className="text-sm text-gray-500">We'll review your request and get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleRequestAccessSubmit} className="space-y-8">
                  {/* Full Name */}
                  <div className="group">
                    <label htmlFor="fullName" className="block text-sm font-semibold leading-6 text-gray-900 mb-3">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className={`block w-full rounded-xl border-0 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset transition-all duration-200 ${
                          formErrors.fullName 
                            ? 'ring-red-500 bg-red-50/50' 
                            : 'ring-gray-300 bg-gray-50/50 hover:bg-white hover:ring-purple-300 focus:ring-2 focus:ring-purple-600 focus:bg-white'
                        } placeholder:text-gray-400 text-base sm:leading-6`}
                        placeholder="Enter your full name"
                      />
                      {formErrors.fullName && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                          </svg>
                          {formErrors.fullName}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Company Name */}
                  <div className="group">
                    <label htmlFor="companyName" className="block text-sm font-semibold leading-6 text-gray-900 mb-3">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="companyName"
                        name="companyName"
                        type="text"
                        required
                        value={formData.companyName}
                        onChange={handleInputChange}
                        className={`block w-full rounded-xl border-0 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset transition-all duration-200 ${
                          formErrors.companyName 
                            ? 'ring-red-500 bg-red-50/50' 
                            : 'ring-gray-300 bg-gray-50/50 hover:bg-white hover:ring-purple-300 focus:ring-2 focus:ring-purple-600 focus:bg-white'
                        } placeholder:text-gray-400 text-base sm:leading-6`}
                        placeholder="Enter your company name"
                      />
                      {formErrors.companyName && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                          </svg>
                          {formErrors.companyName}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Email */}
                  <div className="group">
                    <label htmlFor="email" className="block text-sm font-semibold leading-6 text-gray-900 mb-3">
                      Work Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`block w-full rounded-xl border-0 pl-12 pr-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset transition-all duration-200 ${
                          formErrors.email 
                            ? 'ring-red-500 bg-red-50/50' 
                            : 'ring-gray-300 bg-gray-50/50 hover:bg-white hover:ring-purple-300 focus:ring-2 focus:ring-purple-600 focus:bg-white'
                        } placeholder:text-gray-400 text-base sm:leading-6`}
                        placeholder="Enter your work email address"
                      />
                      {formErrors.email && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                          </svg>
                          {formErrors.email}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Roles Description */}
                  <div className="group">
                    <label htmlFor="roles" className="block text-sm font-semibold leading-6 text-gray-900 mb-3">
                      Roles You're Hiring For <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <textarea
                        id="roles"
                        name="roles"
                        rows={4}
                        required
                        value={formData.roles}
                        onChange={handleInputChange}
                        className={`block w-full rounded-xl border-0 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset transition-all duration-200 resize-none ${
                          formErrors.roles 
                            ? 'ring-red-500 bg-red-50/50' 
                            : 'ring-gray-300 bg-gray-50/50 hover:bg-white hover:ring-purple-300 focus:ring-2 focus:ring-purple-600 focus:bg-white'
                        } placeholder:text-gray-400 text-base sm:leading-6`}
                        placeholder="Briefly describe the roles you're looking to fill (e.g., VP Marketing, Senior Software Engineer, Head of Sales...)"
                      />
                      {formErrors.roles && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                          </svg>
                          {formErrors.roles}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`group relative w-full rounded-xl px-6 py-4 text-base font-semibold text-white shadow-lg transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 ${
                        isSubmitting 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                      }`}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center gap-3">
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Submitting your request...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <span>Request Access</span>
                          <svg className="h-5 w-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                          </svg>
                        </div>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="flex items-center mb-4 sm:mb-0">
              <div className="flex items-center gap-2 mr-3">
                <img src="/burnt-logo.svg" alt="Burnt Logo" className="h-6" />
                <span className="text-lg font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent">burnt</span>
              </div>
              <span className="text-sm text-gray-500">© 2024 Burnt, Inc. All rights reserved.</span>
            </div>
            <div className="flex space-x-6">
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EmployersPage; 